import { FUNNEL, HORIZONTAL_STACKED, PIE_GRADE, LINE, RADAR, BAR, VERTICAL_STACKED, PIE } from 'src/components/Charts/types';
import { chartThemeValidator } from 'src/util/colorPicker/chartThemeValidator';
import { EVOLUTIVE_CARD, EVOLUTIVE_CROSS_CARD, MULTIPLE_CARD, SIMPLE_CARD, SIMPLECROSS_CARD } from 'src/sections/GroupCards/cardTypes';

/**
 * Este módulo foi criado para evitar duplicação de lógica nos gráficos que executam
 * um cálculo para obter o array de cores final que deve ser aplicado.
*/

// Dependendo do tipo de gráfico e do tipo de card, os dados renderizados
// no gráfico podem já estar ordenados em ordem de apresentação ou passaram
// por algum tipo de cálculo para alterar a ordem de visualização, e por isso
// foram criadas duas funções para lidar com cada um desses casos.

// getUnidirectionalColorStrategy(args) leva em consideração os gráficos
// em que o array de dados consumido pelo echarts já está na ordem de apresentação
// correta, por isso um loop simples é realizado.
function getUnidirectionalColorStrategy(data, themeColors, colorPayload, chartIsReversed) {
  
  if (chartIsReversed) {
    return getReversedColorStrategy(data, themeColors, colorPayload);
  }

  const startIndex = 0;
  const calculatedChartColorArray = [];

  for (let i = startIndex; i < data.length; i += 1) {
    // Existe uma cor salva no payload com o nome do item iterado?
    // se sim, essa será a cor alocada para o item do gráfico, caso
    // contrário, irá utilizar a cor do tema padrão.
    calculatedChartColorArray[i] = colorPayload[data[i].name] || themeColors[i];
  }

  return calculatedChartColorArray;
}

// getReversedColorStrategy(args) leva em consideração os gráficos
// em que o array de dados consumido pelo echarts não está na ordem
// correta, nesse caso, a ordem de apresentação é sempre a ordem
// inversa dos dados consumidos, por isso aplicamos ao newColors
// a ordem inversa de cada item do array data.

// Para melhor entendimento, basta debugar o echartOption, mais
// especificamente o atributo "series" retornado pelo gráfico de barra
// no card de gráfico simples, ou o gráfico de barras de cruzamento simples.
function getReversedColorStrategy(data, themeColors, colorPayload) {
  const startIndex = data.length;
  const calculatedChartColorArray = [];

  for (let i = startIndex; i > 0; i -= 1) {
    calculatedChartColorArray[i - 1] = colorPayload[data[i - 1].name] || themeColors[data.length - i];
  }

  return calculatedChartColorArray;
}

// Baseado no tipo do card e no tipo do gráfico atual, será retornado o resultado da execução
// da estratégia de obtenção do array de cores que deve ser consumido pelo gráfico.
const colorSortingStrategies = {
  [`${SIMPLECROSS_CARD}${BAR}`]: (data, themeColors, colorPayload, chartIsReversed) => getReversedColorStrategy(data, themeColors, colorPayload),
  [`${SIMPLE_CARD}${PIE}`]: (data, themeColors, colorPayload, chartIsReversed) => getReversedColorStrategy(data, themeColors, colorPayload),

  default: (data, themeColors, colorPayload, chartIsReversed) => getUnidirectionalColorStrategy(data, themeColors, colorPayload, chartIsReversed),
};

export const cardStrategies = {
  // Os dados do gráfico evolutivo do tipo linha devem ser filtrados
  // para retornar apenas as ocorrências de índice par para evitar duplicações
  // de cores.
  [`${EVOLUTIVE_CARD}${LINE}`]: (referenceData) => (referenceData.filter((item, index) => index % 2 === 0)),
  [`${EVOLUTIVE_CROSS_CARD}${LINE}`]: (referenceData) => (referenceData.filter((item, index) => index % 2 === 0)),
  default: (referenceData) => referenceData || [],
};

function getColorStrategy(currentChart, newData, themeColors, colorPayload, chartIsReversed) {
  if (colorSortingStrategies[currentChart]) {
    return colorSortingStrategies[currentChart](newData, themeColors, colorPayload, chartIsReversed);
  }
  return colorSortingStrategies.default(newData, themeColors, colorPayload, chartIsReversed);
}

const calculateChartsColorsArray = (data, cardType, chartType, chartsColors, parsedTheme, chartIsReversed) => {
  if (!data) {
    return [];
  }
  const currentChart = `${cardType}${chartType}`;
  // Recupera os dados de acordo com o tipo do gráfico (simples | cruzamento multiplo | cruzamento simples | evolutivo)
  const newData = (cardStrategies[currentChart] && cardStrategies[currentChart](data)) || cardStrategies.default(data);
  // Faz uma cópia temporária do objeto de configurações
  // Esse objeto vem do payload de configurações dos gráficos, é o objeto que armazena as cores salvas.
  const chartColorObj = { ...chartsColors };
  const themeColors = chartThemeValidator(parsedTheme, newData.length);

  return getColorStrategy(currentChart, newData, themeColors, chartColorObj, chartIsReversed);
};

// eslint-disable-next-line func-names
export default function calculateChartColorsArray({
  data,
  theme,
  chartsColors,
  chartType = null,
  cardType = SIMPLE_CARD,
  chartIsReversed = false,
}) {
  const parsedTheme = (theme
    && theme.colors
    && theme.colors.charts
    && theme.colors.charts.colors)
  || theme.colors;

  if (data && data.length) {
    const calculatedChartColorArray = calculateChartsColorsArray(
      data,
      cardType,
      chartType,
      chartsColors,
      parsedTheme,
      chartIsReversed
    );

    return calculatedChartColorArray;
  }

  return [];
};
